(() => {
  const MOBILE_QUERY = '(max-width: 749px)';
  const GALLERY_SELECTOR = 'media-gallery';
  const STYLE_ID = 'product-mobile-loop-carousel-smooth-style';

  const MIN_SWIPE_DISTANCE = 45;
  const SWIPE_WIDTH_RATIO = 0.16;

  // Release animation tuning
  const MIN_RELEASE_DURATION = 160;
  const MAX_RELEASE_DURATION = 380;

  function preventClick(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;

    const style = document.createElement('style');
    style.id = STYLE_ID;

    style.textContent = `
      .product-mobile-loop-carousel {
        display: none;
      }

      @media screen and (max-width: 749px) {
        media-gallery.product-mobile-loop-ready slideshow-component {
          display: none !important;
        }

        media-gallery.product-mobile-loop-ready .product-mobile-loop-carousel {
          display: block;
        }

        .product-mobile-loop-carousel {
          position: relative;
          width: 100%;
          overflow: hidden;
          background: var(--color-background, #fff);
        }

        .product-mobile-loop-carousel__viewport {
          position: relative;
          width: 100%;
          overflow: hidden;
          touch-action: pan-y;
          user-select: none;
          -webkit-user-select: none;
          background: inherit;
        }

        .product-mobile-loop-carousel__slide {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          overflow: hidden;
          will-change: transform;
          backface-visibility: hidden;
          transform: translate3d(0, 0, 0);
        }

        .product-mobile-loop-carousel__slide img {
          width: 100%;
          height: 100%;
          display: block;
          object-fit: var(--product-mobile-loop-object-fit, cover);
          object-position: var(--product-mobile-loop-object-position, center);
          pointer-events: none;
          user-select: none;
          -webkit-user-drag: none;
          backface-visibility: hidden;
          transform: translate3d(0, 0, 0);
        }

        .product-mobile-loop-carousel__counter {
          display: none;
          position: absolute;
          right: 14px;
          bottom: 14px;
          z-index: 5;
          padding: 5px 9px;
          border-radius: 999px;
          background: rgb(0 0 0 / 0.45);
          color: #fff;
          font-size: 12px;
          line-height: 1;
          pointer-events: none;
        }
      }
    `;

    document.head.appendChild(style);
  }

  class ProductMobileLoopCarousel {
    constructor(gallery) {
      this.gallery = gallery;
      this.originalSlideshow = gallery.querySelector('slideshow-component');
      this.originalSlides = [];

      this.carousel = null;
      this.viewport = null;
      this.slides = [];
      this.counter = null;

      this.index = 0;
      this.dragX = 0;
      this.width = 0;
      this.viewportHeight = 0;

      this.isTouching = false;
      this.isDragging = false;
      this.isAnimating = false;

      this.startX = 0;
      this.startY = 0;
      this.intentDirection = null;

      this.dragRaf = null;
      this.animationRaf = null;

      this.resizeObserver = null;

      this.init();
    }

    init() {
      if (!this.originalSlideshow) return;

      this.originalSlides = Array.from(
        this.originalSlideshow.querySelectorAll('slideshow-slide')
      );

      if (this.originalSlides.length <= 1) return;

      this.destroyExisting();
      this.build();

      if (!this.carousel || !this.viewport || this.slides.length <= 1) return;

      this.bind();
      this.updateLayout();
      this.applyPositions();
      this.updateCounter();

      this.gallery.classList.add('product-mobile-loop-ready');
    }

    destroyExisting() {
      this.gallery.querySelector('.product-mobile-loop-carousel')?.remove();
    }

    normalizeIndex(index) {
      const total = this.slides.length;
      return (index + total) % total;
    }

    getSlideImage(slide) {
      return slide.querySelector('img');
    }

    getMediaFromSlide(slide) {
      const img = this.getSlideImage(slide);

      if (img) {
        return {
          sourceImage: img,
          src: img.currentSrc || img.src,
          srcset: img.srcset || '',
          sizes: img.sizes || '',
          alt: img.alt || '',
        };
      }

      const poster = slide.querySelector(
        'video[poster], deferred-media img, .deferred-media__poster-image'
      );

      if (poster) {
        return {
          sourceImage: poster,
          src: poster.currentSrc || poster.src || poster.getAttribute('poster'),
          srcset: poster.srcset || '',
          sizes: poster.sizes || '',
          alt: poster.alt || '',
        };
      }

      return null;
    }

    preloadImage(src) {
      if (!src) return;

      const image = new Image();
      image.src = src;
    }

    build() {
      this.carousel = document.createElement('div');
      this.carousel.className = 'product-mobile-loop-carousel';

      this.viewport = document.createElement('div');
      this.viewport.className = 'product-mobile-loop-carousel__viewport';

      this.counter = document.createElement('div');
      this.counter.className = 'product-mobile-loop-carousel__counter';

      this.slides = this.originalSlides
        .map((originalSlide, slideIndex) => {
          const media = this.getMediaFromSlide(originalSlide);
          if (!media || !media.src) return null;

          this.preloadImage(media.src);

          const slide = document.createElement('div');
          slide.className = 'product-mobile-loop-carousel__slide';
          slide.setAttribute('role', 'group');
          slide.setAttribute('aria-label', `${slideIndex + 1} / ${this.originalSlides.length}`);

          const image = media.sourceImage.cloneNode(false);

          image.src = media.src;
          image.alt = media.alt || '';
          image.draggable = false;
          image.loading = slideIndex <= 1 ? 'eager' : 'lazy';
          image.decoding = 'async';

          if (media.srcset) image.srcset = media.srcset;
          if (media.sizes) image.sizes = media.sizes;

          const computed = window.getComputedStyle(media.sourceImage);

          slide.style.setProperty(
            '--product-mobile-loop-object-fit',
            computed.objectFit || 'cover'
          );

          slide.style.setProperty(
            '--product-mobile-loop-object-position',
            computed.objectPosition || 'center'
          );

          slide.appendChild(image);
          this.viewport.appendChild(slide);

          return slide;
        })
        .filter(Boolean);

      if (this.slides.length <= 1) return;

      this.carousel.appendChild(this.viewport);
      this.carousel.appendChild(this.counter);

      this.originalSlideshow.insertAdjacentElement('afterend', this.carousel);
    }

    bind() {
      this.viewport.addEventListener('touchstart', this.onTouchStart, {
        passive: true,
      });

      this.viewport.addEventListener('touchmove', this.onTouchMove, {
        passive: false,
      });

      this.viewport.addEventListener('touchend', this.onTouchEnd, {
        passive: true,
      });

      this.viewport.addEventListener('touchcancel', this.onTouchCancel, {
        passive: true,
      });

      this.resizeObserver = new ResizeObserver(() => {
        this.updateLayout();
        this.applyPositions();
      });

      this.resizeObserver.observe(this.viewport);
    }

    updateLayout() {
      const originalRect =
        this.originalSlides[0]?.getBoundingClientRect() ||
        this.originalSlideshow?.getBoundingClientRect();

      this.width =
        this.viewport.getBoundingClientRect().width ||
        originalRect?.width ||
        window.innerWidth;

      if (originalRect?.height && originalRect.height > 20) {
        this.viewportHeight = originalRect.height;
        this.viewport.style.height = `${this.viewportHeight}px`;
      } else if (this.viewportHeight) {
        this.viewport.style.height = `${this.viewportHeight}px`;
      } else {
        this.viewport.style.aspectRatio = '1 / 1.25';
      }
    }

    getVirtualOffset(slideIndex) {
      const total = this.slides.length;
      let offset = slideIndex - this.index;

      if (offset > total / 2) {
        offset -= total;
      } else if (offset < -total / 2) {
        offset += total;
      }

      // Tie case for 2 slides / even number of slides
      if (Math.abs(offset) === total / 2) {
        if (this.intentDirection === 'next' && offset < 0) {
          offset += total;
        }

        if (this.intentDirection === 'previous' && offset > 0) {
          offset -= total;
        }
      }

      return offset;
    }

    applyPositions() {
      this.slides.forEach((slide, slideIndex) => {
        const offset = this.getVirtualOffset(slideIndex);
        const x = offset * this.width + this.dragX;

        slide.style.transition = 'none';
        slide.style.transform = `translate3d(${x}px, 0, 0)`;
        slide.style.zIndex = Math.abs(offset) <= 1 ? '2' : '1';
        slide.setAttribute('aria-hidden', slideIndex === this.index ? 'false' : 'true');
      });
    }

    requestPositionUpdate() {
      if (this.dragRaf || this.isAnimating) return;

      this.dragRaf = requestAnimationFrame(() => {
        this.dragRaf = null;

        if (this.isAnimating) return;

        this.applyPositions();
      });
    }

    cancelPositionUpdate() {
      if (!this.dragRaf) return;

      cancelAnimationFrame(this.dragRaf);
      this.dragRaf = null;
    }

    cancelReleaseAnimation() {
      if (!this.animationRaf) return;

      cancelAnimationFrame(this.animationRaf);
      this.animationRaf = null;
    }

    updateCounter() {
      if (!this.counter) return;
      this.counter.textContent = `${this.index + 1} / ${this.slides.length}`;
    }

    syncOriginalSlideshow() {
      if (typeof this.originalSlideshow?.select !== 'function') return;

      try {
        this.originalSlideshow.select(this.index, undefined, {
          animate: false,
        });
      } catch (error) {
        // Keep custom carousel independent from Horizon internals
      }
    }

    getReleaseDuration(distance) {
      const remaining = Math.min(1, Math.abs(distance) / this.width);

      return Math.round(
        Math.max(
          MIN_RELEASE_DURATION,
          Math.min(MAX_RELEASE_DURATION, remaining * MAX_RELEASE_DURATION)
        )
      );
    }

    easeOut(t) {
      // Smooth deceleration
      return 1 - Math.pow(1 - t, 4);
    }

    animateDragTo(finalDragX) {
      if (this.isAnimating) return Promise.resolve(false);

      this.isAnimating = true;

      this.cancelPositionUpdate();
      this.cancelReleaseAnimation();

      const startDragX = this.dragX;
      const distance = finalDragX - startDragX;
      const duration = this.getReleaseDuration(distance);
      const startTime = performance.now();

      return new Promise((resolve) => {
        const step = (now) => {
          const elapsed = now - startTime;
          const progress = Math.min(1, elapsed / duration);
          const eased = this.easeOut(progress);

          this.dragX = startDragX + distance * eased;
          this.applyPositions();

          if (progress < 1) {
            this.animationRaf = requestAnimationFrame(step);
            return;
          }

          this.animationRaf = null;
          this.dragX = finalDragX;
          this.applyPositions();

          this.isAnimating = false;
          resolve(true);
        };

        this.animationRaf = requestAnimationFrame(step);
      });
    }

    async settleTo(nextIndex, direction) {
      this.intentDirection = direction;

      const finalDragX = direction === 'next' ? -this.width : this.width;

      await this.animateDragTo(finalDragX);

      this.index = this.normalizeIndex(nextIndex);
      this.dragX = 0;
      this.intentDirection = null;

      this.applyPositions();
      this.updateCounter();
      this.syncOriginalSlideshow();
    }

    async snapBack() {
      await this.animateDragTo(0);

      this.dragX = 0;
      this.intentDirection = null;

      this.applyPositions();
    }

    onTouchStart = (event) => {
      if (!window.matchMedia(MOBILE_QUERY).matches) return;
      if (this.isAnimating) return;
      if (event.touches.length !== 1) return;

      const touch = event.touches[0];

      this.isTouching = true;
      this.isDragging = false;

      this.startX = touch.clientX;
      this.startY = touch.clientY;

      this.dragX = 0;
      this.intentDirection = null;

      this.cancelPositionUpdate();
      this.cancelReleaseAnimation();

      this.applyPositions();
    };

    onTouchMove = (event) => {
      if (!this.isTouching || this.isAnimating) return;

      const touch = event.touches[0];
      if (!touch) return;

      const diffX = touch.clientX - this.startX;
      const diffY = touch.clientY - this.startY;

      if (!this.isDragging) {
        if (Math.abs(diffX) < 6) return;

        if (Math.abs(diffX) <= Math.abs(diffY)) {
          this.cancelDrag(false);
          return;
        }

        this.isDragging = true;

        document.addEventListener('click', preventClick, {
          once: true,
          capture: true,
        });
      }

      if (event.cancelable) {
        event.preventDefault();
      }

      this.intentDirection = diffX < 0 ? 'next' : 'previous';
      this.dragX = diffX;

      this.requestPositionUpdate();
    };

    onTouchEnd = async (event) => {
      if (!this.isTouching || this.isAnimating) return;

      const touch = event.changedTouches[0];

      this.isTouching = false;
      this.cancelPositionUpdate();

      if (!touch) {
        await this.snapBack();
        this.isDragging = false;
        return;
      }

      const diffX = touch.clientX - this.startX;
      const diffY = touch.clientY - this.startY;

      const threshold = Math.max(MIN_SWIPE_DISTANCE, this.width * SWIPE_WIDTH_RATIO);

      const isSwipe =
        Math.abs(diffX) > threshold &&
        Math.abs(diffX) > Math.abs(diffY);

      if (!this.isDragging || !isSwipe) {
        await this.snapBack();
        this.isDragging = false;
        return;
      }

      if (diffX < 0) {
        await this.settleTo(this.index + 1, 'next');
      } else {
        await this.settleTo(this.index - 1, 'previous');
      }

      this.isDragging = false;
    };

    onTouchCancel = () => {
      this.cancelDrag(true);
    };

    cancelDrag(animate = true) {
      this.isTouching = false;
      this.isDragging = false;

      this.cancelPositionUpdate();

      if (animate) {
        this.snapBack();
      } else {
        this.cancelReleaseAnimation();
        this.dragX = 0;
        this.intentDirection = null;
        this.applyPositions();
      }
    }

    destroy() {
      this.cancelPositionUpdate();
      this.cancelReleaseAnimation();

      this.resizeObserver?.disconnect();
      this.carousel?.remove();

      this.gallery.classList.remove('product-mobile-loop-ready');
    }
  }

  const instances = new WeakMap();

  function initProductMobileLoopCarousels(root = document) {
    injectStyles();

    root.querySelectorAll(GALLERY_SELECTOR).forEach((gallery) => {
      if (instances.has(gallery)) return;

      const instance = new ProductMobileLoopCarousel(gallery);

      if (instance.carousel) {
        instances.set(gallery, instance);
      }
    });
  }

  function refreshProductMobileLoopCarousels(root = document) {
    root.querySelectorAll(GALLERY_SELECTOR).forEach((gallery) => {
      const existing = instances.get(gallery);

      if (existing) {
        existing.destroy();
        instances.delete(gallery);
      }
    });

    initProductMobileLoopCarousels(root);
  }

  document.addEventListener('DOMContentLoaded', () => {
    initProductMobileLoopCarousels();
  });

  document.addEventListener('shopify:section:load', (event) => {
    initProductMobileLoopCarousels(event.target);
  });

  document.addEventListener('change', (event) => {
    if (!event.target.closest('variant-picker, product-form, media-gallery')) return;

    window.setTimeout(() => {
      refreshProductMobileLoopCarousels();
    }, 250);
  });
})();